import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Col, Flex, Form, Input, message, Popconfirm, Row, Typography } from "antd";
import { deleteBundle, getBundles } from "api/bundles";
import { LoadingSpinner } from "components/LoadingSpinner";
import { AMOUNT_PER_CREDIT } from "constants/common";
import { ConfigureBundleModal } from "modals/ConfigureBundleModal";
import { useEffect, useRef, useState } from "react";
import classes from "./Credits.module.css";

type Bundle = {
  id: string;
  description: string;
  credit: number;
  amount: number;
  removed?: boolean;
  active?: boolean;
  updated?: boolean;
};

export const Credits: React.FC = () => {
  const [bundles, setBundles] = useState<Bundle[]>([]);
  const [getBundlesLoading, setGetBundlesLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const selectedBundle = useRef<Bundle>({
    id: "",
    amount: 0,
    credit: 0,
    description: ""
  });

  const fetchBundles = async () => {
    setGetBundlesLoading(true);
    const result = await getBundles();
    setBundles(result.data.data);
    setGetBundlesLoading(false);
  };

  useEffect(() => {
    (async () => {
      await fetchBundles();
    })();
  }, []);

  const isDuplicateBundle = (id: string, credit: number, amount: number) => {
    if(!bundles || !bundles.length) return false;

    return bundles.some((b) => {
      return b.id !== id && b.credit === credit && b.amount === amount;
    });
  };

  if (getBundlesLoading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <Row>
        <Col span={10}>
          <Typography.Title
            style={{
              fontSize: "16px",
              fontWeight: 600,
              lineHeight: "23.2px"
            }}>
            Configure credits
          </Typography.Title>
          <Typography style={{ fontSize: "14px", fontWeight: 400, color: "#B5B5B5" }}>
            Configure the credits that can be purchased by the users.
          </Typography>
        </Col>
        <Col span={14}>
          <Flex justify="flex-end">
            <Button
              style={{
                border: "none",
                borderRadius: "8px",
                padding: "10px 16px",
                display: "flex",
                alignItems: "center",
                fontFamily: "Titillium Web",
                fontSize: "14px",
                fontWeight: "600",
                lineHeight: "20px",
                textAlign: "left",
                color: "#fff",
                marginBottom: "32px"
              }}
              icon={<PlusOutlined />}
              onClick={() => {
                selectedBundle.current = {
                  id: "temp__" + new Date().toString(),
                  description: "",
                  amount: AMOUNT_PER_CREDIT.toNumber(),
                  credit: 1,
                  removed: false
                };
                setShowModal(true);
              }}>
              Add new bundle
            </Button>
          </Flex>
          <>
            <Form layout="vertical">
              {bundles
                ?.filter((bundle) => !bundle.removed)
                .map((bundle, index) => (
                  <Row className={classes.bundles} key={bundle.id}>
                    <Row className={classes.bundleTitle} justify="space-between" align="middle">
                      <Typography.Text
                        style={{ width: "50%" }}
                        title={bundle.description}
                        ellipsis
                        className={classes.bundleName}>
                        {bundle.description}
                      </Typography.Text>
                      <Flex align="center">
                        <Button
                          icon={<EditOutlined />}
                          type="text"
                          style={{ color: "#E75012" }}
                          onClick={() => {
                            selectedBundle.current = bundle;
                            setShowModal(true);
                          }}>
                          Edit
                        </Button>
                        <Popconfirm
                          title={"Are you sure you want to delete the bundle?"}
                          okText={"Yes"}
                          cancelText={"No"}
                          onConfirm={async () => {
                            try {
                              const isTemp = bundle.id.startsWith("temp__");
                              if (!isTemp) {
                                await deleteBundle(bundle.id);
                                message.success("Bundle deleted successfully");
                                await fetchBundles();
                              } else {
                                setBundles(bundles.filter(({ id }) => id !== bundle.id));
                              }
                            } catch (er) {
                              console.log(er);
                            }
                          }}>
                          <Button
                            type="text"
                            size="small"
                            icon={<DeleteOutlined />}
                            style={{
                              color: "red",
                              background: "transparent",
                              border: "none",
                              boxShadow: ""
                            }}
                          />
                        </Popconfirm>
                      </Flex>
                    </Row>
                    <Row className={classes.InputContainer}>
                      <Form.Item className={classes.inputBox} label="Credits">
                        <Input
                          readOnly
                          value={bundle.credit}
                          style={{
                            height: "44px"
                          }}
                        />
                      </Form.Item>
                    </Row>

                    <Row className={classes.InputContainer}>
                      <Form.Item className={classes.inputBox} label="Amount in euros">
                        <Input
                          readOnly
                          style={{
                            height: "44px"
                          }}
                          value={bundle.amount}
                        />
                        {AMOUNT_PER_CREDIT.mul(bundle.credit).toNumber() === bundle.amount && (
                          <Typography.Text className={classes.customBundleInfoText}>
                            Calculated based on 1 credit = {AMOUNT_PER_CREDIT.toNumber()} euros.
                            Adjust manually if 'Customize Bundle' is enabled
                          </Typography.Text>
                        )}
                      </Form.Item>
                    </Row>
                  </Row>
                ))}
            </Form>
          </>

          {showModal && (
            <ConfigureBundleModal
              showModal={showModal}
              fetchBundles={fetchBundles}
              onCancel={() => setShowModal(false)}
              isDuplicateBundle={isDuplicateBundle}
              bundle={selectedBundle.current}
            />
          )}
        </Col>
      </Row>
    </>
  );
};
